import React, { useState, useEffect, useCallback } from "react";
import { StepperHeader } from "./StepperHeader";
import { StepperLegend } from "./StepperLegend";
import { StepNavigation } from "./StepNavigation";
import { InfoBox } from "newui/components/IconBox";
import type { StepProps } from "./Step";

interface StepContainerProps {
	children: React.ReactNode;
	highestStep?: number;
	initialStep?: number;
	isSubmitting?: boolean;
	showLegend?: boolean;
	onComplete?: () => void;
	onStepChange?: (step: number) => void;
	/**
	 * Lets a step element advance the stepper programmatically (without the user
	 * clicking Next) — e.g. a self-contained step that completes via its own
	 * internal buttons. StepContainer registers a bounds-checked "go to next
	 * step" function here on every render; the parent stores it and calls it
	 * when its step signals completion.
	 */
	registerGoToNextStep?: (goToNextStep: () => void) => void;
}

export const StepContainer: React.FC<StepContainerProps> = ({
	children,
	highestStep: highestStepProp,
	onComplete,
	onStepChange,
	registerGoToNextStep,
	initialStep = 0,
	isSubmitting = false,
	showLegend = true,
}) => {
	const [currentStep, setCurrentStep] = useState(initialStep);
	/**
	 * Guards against a second Next click while `onBeforeNext` is still awaiting.
	 * The evaluation stepper's onBeforeNext POSTs advance-step, and the backend
	 * advances relative to ITS OWN current_step — so a double-click advanced twice
	 * and the user landed on Revisions after clicking "Proceed to Clarification
	 * Review". The UI only ever moves +1, so the overshoot showed up when the
	 * initialStep effect below re-synced to the backend's position.
	 */
	const [isAdvancing, setIsAdvancing] = useState(false);

	useEffect(() => {
		setCurrentStep(initialStep);
	}, [initialStep]);

	const steps = React.Children.toArray(children).filter(
		(child): child is React.ReactElement<StepProps> =>
			React.isValidElement(child)
	);
	const stepCount = steps.length;

	const stepMeta = steps.map((step) => ({
		name: step.props.name,
		description: step.props.description,
		enabled: step.props.enabled,
		nextLabel: step.props.nextLabel,
		displayNumberOverride: step.props.displayNumberOverride,
		onBeforeNext: step.props.onBeforeNext,
	}));

	const highestStep = highestStepProp ?? currentStep;
	const activeStep = steps[currentStep];
	const currentStepEnabled = stepMeta[currentStep]?.enabled ?? false;
	const currentNextLabel = stepMeta[currentStep]?.nextLabel;
	const currentReadOnly = steps[currentStep]?.props?.readOnly ?? false;
	const currentHideNext = steps[currentStep]?.props?.hideNext ?? false;

	const changeStep = useCallback((step: number): void => {
		setCurrentStep(step);
		if (onStepChange) {
			onStepChange(step);
		}
	}, [onStepChange]);

	// Programmatic "advance to the next step" — same bounds the Next button uses,
	// but without the onBeforeNext gate (the caller has already completed the step
	// via its own internal flow). Re-created when currentStep changes and
	// re-registered with the parent so it always advances from the latest step.
	const goToNextStep = useCallback((): void => {
		if (currentStep < stepCount - 1) {
			changeStep(currentStep + 1);
		}
	}, [currentStep, stepCount, changeStep]);

	useEffect(() => {
		if (registerGoToNextStep) {
			registerGoToNextStep(goToNextStep);
		}
	}, [registerGoToNextStep, goToNextStep]);

	const handleStepClick = (index: number): void => {
		if (index <= highestStep) {
			changeStep(index);
		}
	};

	const handleBack = (): void => {
		if (currentStep > 0) {
			changeStep(currentStep - 1);
		}
	};

	const handleNext = async (): Promise<void> => {
		if (isAdvancing) {
			return;
		}
		if (currentStep < steps.length - 1 && currentStepEnabled) {
			const beforeNext = stepMeta[currentStep]?.onBeforeNext;
			if (beforeNext) {
				setIsAdvancing(true);
				let proceed: boolean;
				try {
					proceed = await beforeNext();
				} catch {
					// A throwing onBeforeNext previously escaped as an unhandled
					// rejection AND left the guard set. Treat it as "do not proceed";
					// the handler surfaces its own error message to the user.
					proceed = false;
				} finally {
					setIsAdvancing(false);
				}
				if (!proceed) {
					return;
				}
			}
			changeStep(currentStep + 1);
		}
	};

	const handleComplete = (): void => {
		if (onComplete) {
			onComplete();
		}
	};

	return (
		<div>
			<StepperHeader
				steps={stepMeta}
				currentStep={currentStep}
				highestStep={highestStep}
				onStepClick={handleStepClick}
			/>
			{showLegend && <StepperLegend />}
			<div className="py-2">{activeStep}</div>
			<StepNavigation
				currentStep={currentStep}
				totalSteps={steps.length}
				currentStepEnabled={currentStepEnabled}
				isSubmitting={isSubmitting || isAdvancing}
				nextLabel={currentNextLabel}
				readOnly={currentReadOnly}
				hideNext={currentHideNext}
				onBack={handleBack}
				onNext={handleNext}
				onComplete={handleComplete}
			/>
		</div>
	);
};
